import clsx from 'clsx';
import { useIdentityStore } from '@/store/identity';
import { shortId } from '@/lib/format';
import { Spinner } from '@/components/ui/Spinner';

export interface HeaderProps {
  /** 网络状态灯文案 */
  networkLabel?: string;
  networkTone?: 'ok' | 'warn' | 'err';
  onLock?: () => void;
}

const TONE_DOT: Record<'ok' | 'warn' | 'err', string> = {
  ok: 'bg-ok',
  warn: 'bg-warn',
  err: 'bg-err',
};

/** 顶部栏：Logo + 网络状态灯 + 锁定入口。 */
export function Header({ networkLabel = '离线', networkTone = 'err', onLock }: HeaderProps): JSX.Element {
  const did = useIdentityStore((s) => s.did);
  const name = useIdentityStore((s) => s.name);

  return (
    <header className="flex h-14 shrink-0 items-center justify-between border-b border-line bg-panel px-4">
      <div className="flex items-center gap-2">
        <span className="text-base font-semibold text-accent">MeshLink</span>
        <span className="hidden text-xs text-dim md:inline">去中心化即时通讯</span>
      </div>

      <div className="flex items-center gap-3">
        <span className="flex items-center gap-1.5 text-xs text-dim" title={networkLabel}>
          <span className={clsx('inline-block h-2 w-2 rounded-full', TONE_DOT[networkTone])} />
          {networkLabel}
        </span>
        <span className="hidden text-xs text-dim sm:inline">{name || shortId(did, 10, 4)}</span>
        {onLock ? (
          <button
            type="button"
            className="rounded-btn bg-panel-2 px-2 py-1 text-xs text-dim hover:bg-line"
            onClick={onLock}
            title="立即锁定"
          >
            锁定
          </button>
        ) : null}
      </div>
    </header>
  );
}

/** 顶部路由进度条（T05 收尾，先给出占位实现）。 */
export function RouteProgress({ active }: { active: boolean }): JSX.Element | null {
  if (!active) return null;
  return (
    <div className="fixed left-0 top-0 z-50 h-0.5 w-full bg-accent/30">
      <div className="h-full w-1/3 animate-pulse bg-accent" />
      <span className="sr-only">
        <Spinner size={10} />
      </span>
    </div>
  );
}

export default Header;
